import { Users, Clock, Star, UserPlus, Briefcase, Award, Brain, AlertTriangle, Link } from 'lucide-react';
import { StatCard } from '@/components/Card';
import { StatusBadge } from '@/components/StatusBadge';
import { useApi } from '@/hooks/useApi';
import { EmptyState } from '@/components/EmptyState';
import { Link as RouterLink } from 'react-router-dom';

interface TheoBriefingSummary {
  summary: {
    total_at_risk: number;
    critical_count: number;
  };
  top_risks: Array<{
    employee_name: string;
    risk_level: string;
    risk_score: number;
  }>;
}

interface DashboardData {
  kpis: {
    total_employees: number;
    pending_pto_requests: number;
    overdue_reviews: number;
    active_onboarding: number;
    open_positions: number;
    expiring_certifications: number;
  };
  pending_actions: {
    pto_requests: Array<{ id: string; employee_name: string; time_off_type: string; start_date: string; end_date: string }>;
    overdue_reviews: Array<{ id: string; employee_name: string; due_date: string }>;
    probation_ending: Array<{ id: string; first_name: string; last_name: string; probation_end_date: string }>;
  };
  recent_hires: Array<{ id: string; first_name: string; last_name: string; title: string; department_name: string; hire_date: string }>;
  open_jobs: Array<{ id: string; title: string; application_count: number }>;
}

export function DashboardPage() {
  const { data, loading } = useApi<DashboardData>('dashboard');
  const { data: theoBriefing } = useApi<TheoBriefingSummary>('people/theo/briefing');

  if (loading) return <div className="text-muted-foreground">Loading...</div>;
  if (!data) return <div className="text-muted-foreground">No data</div>;

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard label="Employees" value={data.kpis.total_employees} icon={<Users className="w-5 h-5" />} accent />
        <StatCard label="Pending PTO" value={data.kpis.pending_pto_requests} icon={<Clock className="w-5 h-5" />} />
        <StatCard label="Overdue Reviews" value={data.kpis.overdue_reviews} icon={<Star className="w-5 h-5" />} />
        <StatCard label="Onboarding" value={data.kpis.active_onboarding} icon={<UserPlus className="w-5 h-5" />} />
        <StatCard label="Open Positions" value={data.kpis.open_positions} icon={<Briefcase className="w-5 h-5" />} />
        <StatCard label="Expiring Certs" value={data.kpis.expiring_certifications} icon={<Award className="w-5 h-5" />} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Pending PTO */}
        <div className="glass rounded-xl p-5">
          <h3 className="text-sm font-medium text-foreground mb-3">Pending PTO Requests</h3>
          {data.pending_actions.pto_requests.length === 0 ? (
            <EmptyState icon={<Clock className="h-5 w-5" />} title="No pending requests" />
          ) : (
            <div className="space-y-2">
              {data.pending_actions.pto_requests.map(r => (
                <div key={r.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                  <div>
                    <p className="text-sm text-foreground">{r.employee_name}</p>
                    <p className="text-xs text-muted-foreground">{r.time_off_type} &middot; {r.start_date} to {r.end_date}</p>
                  </div>
                  <StatusBadge status="pending" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Hires */}
        <div className="glass rounded-xl p-5">
          <h3 className="text-sm font-medium text-foreground mb-3">Recent Hires</h3>
          {data.recent_hires.length === 0 ? (
            <EmptyState icon={<UserPlus className="h-5 w-5" />} title="No recent hires" />
          ) : (
            <div className="space-y-2">
              {data.recent_hires.map(e => (
                <div key={e.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                  <div>
                    <p className="text-sm text-foreground">{e.first_name} {e.last_name}</p>
                    <p className="text-xs text-muted-foreground">{e.title} &middot; {e.department_name}</p>
                  </div>
                  <span className="text-xs text-muted-foreground">{e.hire_date}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Open Positions */}
        <div className="glass rounded-xl p-5">
          <h3 className="text-sm font-medium text-foreground mb-3">Open Positions</h3>
          {data.open_jobs.length === 0 ? (
            <EmptyState icon={<Briefcase className="h-5 w-5" />} title="No open positions" />
          ) : (
            <div className="space-y-2">
              {data.open_jobs.map(j => (
                <div key={j.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                  <p className="text-sm text-foreground">{j.title}</p>
                  <span className="text-xs text-muted-foreground">{j.application_count} applicants</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Overdue Reviews */}
        <div className="glass rounded-xl p-5">
          <h3 className="text-sm font-medium text-foreground mb-3">Overdue Reviews</h3>
          {data.pending_actions.overdue_reviews.length === 0 ? (
            <EmptyState icon={<Star className="h-5 w-5" />} title="No overdue reviews" />
          ) : (
            <div className="space-y-2">
              {data.pending_actions.overdue_reviews.map(r => (
                <div key={r.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                  <p className="text-sm text-foreground">{r.employee_name}</p>
                  <span className="text-xs text-red-400">Due {r.due_date.split('T')[0]}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* THEO HR Widget */}
      {theoBriefing && (
        <div className="glass rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Brain className="w-4 h-4 text-amber-500" />
              <h3 className="text-sm font-medium text-foreground">THEO HR Advisor</h3>
            </div>
            <RouterLink to="/theo-hr" className="text-xs text-amber-500 hover:text-amber-400 flex items-center gap-1">
              View Full Report <Link className="w-3 h-3" />
            </RouterLink>
          </div>
          {theoBriefing.summary.total_at_risk === 0 ? (
            <div className="flex items-center gap-2 text-green-400 text-sm">
              <span>No retention risks detected this week</span>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground mb-2">
                {theoBriefing.summary.critical_count > 0 && (
                  <span className="text-red-400 font-medium">{theoBriefing.summary.critical_count} critical </span>
                )}
                {theoBriefing.summary.total_at_risk} employee{theoBriefing.summary.total_at_risk > 1 ? 's' : ''} at retention risk
              </p>
              {theoBriefing.top_risks.slice(0, 3).map((risk, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-border/50 last:border-0">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className={`w-3.5 h-3.5 ${risk.risk_level === 'critical' ? 'text-red-400' : 'text-amber-400'}`} />
                    <span className="text-sm text-foreground">{risk.employee_name}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">Score: {risk.risk_score}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
